import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";

const IllustrationsContext = createContext(null);

export const IllustrationsProvider = ({ children }) => {
  const [map, setMap] = useState({});

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/illustrations");
      const m = {};
      for (const item of data) m[item.key] = item;
      setMap(m);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ map, refresh }), [map, refresh]);
  return <IllustrationsContext.Provider value={value}>{children}</IllustrationsContext.Provider>;
};

export const useIllustrations = () => useContext(IllustrationsContext);

export const Illustration = ({ keyName, fallback = null, className = "", alt = "" }) => {
  const ctx = useIllustrations();
  const item = ctx?.map?.[keyName];
  if (!item) return fallback;
  return (
    <img
      src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/illustrations/${item.key}`}
      alt={alt || keyName}
      loading="lazy"
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
};

/**
 * Canonical illustration keys with default 4K comic prompts.
 * Admin can regenerate individually from the Illustrations page.
 */
export const ILLUSTRATION_KEYS = [
  { key: "carlos_portrait",       label: "Carlos (Mentor)",        prompt: "A friendly Latino male sales mentor named Carlos, mid-30s, wearing a blue polo with yellow Canales Furniture logo, warm confident smile, standing proud with arms open in a welcoming gesture, comic-book style, bold outlines, primary colors, cream background." },
  { key: "ana_portrait",          label: "Ana (Learner)",          prompt: "A cheerful young Latina female trainee named Ana, mid-20s, ponytail, wearing a red Canales Furniture polo, holding a small notebook, bright eyes, eager expression, comic-book style, bold outlines, primary colors, cream background." },
  { key: "mascot",                label: "Sillón Canales (Mascot)",prompt: "A cute anthropomorphic blue sofa mascot with a friendly cartoon face, big eyes and warm smile, small yellow bow tie, comic-book style, bold black outlines, cream background, dynamic pose." },
  { key: "gonzalez_family",       label: "Familia González",       prompt: "A happy Hispanic family of four (dad, mom, two kids) shopping for furniture at Canales, all smiling, casual clothes, comic-book cartoon style, bold outlines, cream background." },
  { key: "cover",                 label: "Portada",                prompt: "Comic-book magazine cover for 'Canales Furniture — La Revista', featuring Carlos and Ana standing next to a big blue smiling sofa mascot, giant title area at the top, primary colors, halftone dot pattern, dynamic and vibrant." },
  { key: "chapter_prep",          label: "Cap. Preparación",       prompt: "A Canales Furniture store at dawn, Carlos and Ana preparing the sales floor, checking price tags and promotions, cheerful cartoon comic style, cream background, bold outlines." },
  { key: "chapter_greet",         label: "Cap. GREET",             prompt: "Carlos smiling and waving to the González family as they enter the store, warm welcoming gesture, comic-book style, cream background, bold outlines." },
  { key: "chapter_discover",     label: "Cap. DISCOVER",          prompt: "Ana attentively listening to Mrs. González describing her family's needs, with a small notebook, question mark speech bubbles above their heads, comic-book style, cream background." },
  { key: "chapter_demonstrate",   label: "Cap. DEMONSTRATE",       prompt: "Mr. González sitting on a comfy blue sofa with eyes closed, big smile, while Carlos gestures explaining, comic-book style, cream background, bold outlines." },
  { key: "chapter_close",         label: "Cap. CLOSE",             prompt: "Carlos handing over a delivery calendar to a happy customer signing paperwork, a sold sticker on a sofa in background, comic-book style, cream background." },
  { key: "chapter_followup",      label: "Cap. FOLLOW-UP",         prompt: "Carlos on the phone smiling, small speech bubble reading '¡Gracias!' next to a happy customer at home relaxing on a Canales sofa, split-panel comic-book style, cream background." },
  { key: "chapter_complaints",    label: "Cap. Quejas",            prompt: "An initially upset customer being calmly listened to by Ana with empathy, the customer's mood turning positive by the end, split-panel comic-book style, cream background." },
  { key: "chapter_communication", label: "Cap. Comunicación",      prompt: "Ana drawing on a napkin to explain furniture dimensions to an elderly customer who is smiling and understanding, comic-book style, cream background." },
];
